// One minor league game between two organizations' affiliates, simulated half-inning by half-inning but statistical
// only (no box score, no play-by-play stored). Every plate appearance is ONE event between the batter and the current
// pitcher; that single event updates the batter's line, the pitcher's line, the bases, the score, RBI and earned runs,
// so the two sides can't disagree: hits for = hits against, runs = score, outs = innings, and so on.
//
// Odds: the previous model's formulas with both sides at once. Each rating is a z-score against the minor league
// population; batter terms (contact, power, eye, speed) and pitcher terms (power, control, movement) are added, plus a
// small team-defense term on balls in play. With an average opponent it reduces exactly to the old one-sided model.
//
// Who plays: each player fills at most one spot. Regulars rest now and then (catchers more), bench players fill in;
// the rotation turns in order and relievers don't pitch two games in a row. A spot nobody real can fill (short or
// injured roster, empty bullpen) goes to an anonymous replacement-level player: his events count in the score and in
// the team's "replacement" totals, never on a real player, and no player, contract or stat line is created for him.
// No catcher-capable player: the best available position player catches, out of position (weaker defense).
import type { MinorStanding, MinorStats, Player } from "../../../common/types.ts";
export type { MinorStanding };
import { emptyMinorStats } from "../../../common/minorStats.ts";
import { canStart, coversPosition, isPitcherGroup } from "../team/minorsComposition.baseball.ts";

const clamp = (x: number, a: number, b: number) => Math.max(a, Math.min(b, x));
const rt = (p: Player) => p.ratings.at(-1)! as any;

export type Context = Record<"con" | "hpw" | "eye" | "spd" | "ppw" | "ctl" | "mov" | "def", { mean: number; sd: number }>;
const meanSd = (xs: number[]) => {
	if (xs.length === 0) return { mean: 50, sd: 15 };
	const mean = xs.reduce((a, b) => a + b, 0) / xs.length;
	const sd = Math.sqrt(xs.reduce((a, b) => a + (b - mean) ** 2, 0) / xs.length);
	return { mean, sd: Math.max(5, sd) };
};
// Fielding at a position from the underlying ratings (catching for C; range/hands/arm elsewhere)
const defenseAt = (p: Player, pos: string) => {
	const x = rt(p);
	if (pos === "C") return x.cat;
	if (["LF", "CF", "RF"].includes(pos)) return (x.fly + x.spd + x.thr) / 3;
	return (x.gnd + x.thr + x.spd) / 3;
};
// Competition quality: the minor league population of the day
export const getContext = (players: Player[]): Context => {
	const hitters = players.filter((p) => !isPitcherGroup(p));
	const pitchers = players.filter(isPitcherGroup);
	const of = (list: Player[], key: string) => meanSd(list.map((p) => rt(p)[key] as number));
	return {
		con: of(hitters, "con"), hpw: of(hitters, "hpw"), eye: of(hitters, "eye"), spd: of(hitters, "spd"),
		ppw: of(pitchers, "ppw"), ctl: of(pitchers, "ctl"), mov: of(pitchers, "mov"),
		def: meanSd(hitters.map((p) => defenseAt(p, rt(p).pos))),
	};
};
export const REPLACEMENT_Z = -1; // replacement level: one standard deviation below the minor league average
const zOf = (ctx: Context, p: Player | undefined, key: keyof Context) =>
	p ? clamp(((rt(p)[key] as number) - ctx[key].mean) / ctx[key].sd, -3, 3) : REPLACEMENT_Z;

export const emptyStanding = (startedMidSeason: boolean): MinorStanding => ({
	g: 0, w: 0, l: 0, rs: 0, ra: 0, rep: { pa: 0, bf: 0, outs: 0 }, gids: [], startedMidSeason,
});

const line = (p: Player, season: number): MinorStats => {
	if (!p.minorStats || p.minorStats.season !== season) {
		p.minorStats = emptyMinorStats(season);
	}
	return p.minorStats;
};

type Batter = { p: Player | undefined; pos: string };
const REST_CATCHER = 0.22;
const REST_OTHER = 0.1;
const FIELD_ORDER = ["C", "SS", "CF", "2B", "3B", "RF", "LF", "1B"]; // scarce positions first

// Lineup: each player in one spot; a regular may rest when it doesn't leave a spot to a replacement
export const buildLineup = (ctx: Context, hitters: Player[], random: () => number) => {
	const catchers = hitters.filter((p) => coversPosition(p, "C"));
	// The only real catcher doesn't rest (otherwise someone would have to catch out of position)
	const rested = hitters.filter((p) => (catchers.length === 1 && catchers[0] === p) || random() >= (rt(p).pos === "C" ? REST_CATCHER : REST_OTHER));
	const pool = rested.length >= 9 ? rested : hitters;
	const used = new Set<Player>();
	const lineup: Batter[] = [];
	for (const pos of FIELD_ORDER) {
		const free = pool.filter((p) => !used.has(p));
		const able = free.filter((p) => coversPosition(p, pos));
		const pick = (able.length > 0 ? able : free).sort((a, b) => (rt(b).ovrs?.[pos] ?? 0) - (rt(a).ovrs?.[pos] ?? 0) || a.pid - b.pid)[0];
		if (pick) used.add(pick);
		lineup.push({ p: pick, pos });
	}
	const dh = pool.filter((p) => !used.has(p)).sort((a, b) => rt(b).ovr - rt(a).ovr || a.pid - b.pid)[0];
	if (dh) used.add(dh);
	lineup.push({ p: dh, pos: "DH" });
	const defense = lineup.filter((b) => b.pos !== "DH").reduce((s, b) => s + (b.p ? clamp((defenseAt(b.p, b.pos) - ctx.def.mean) / ctx.def.sd, -3, 3) : REPLACEMENT_Z), 0) / 8;
	const bat = (b: Batter) => (b.p ? rt(b.p).con + rt(b.p).hpw + rt(b.p).eye : -1);
	lineup.sort((a, b) => bat(b) - bat(a) || (a.p?.pid ?? 1e9) - (b.p?.pid ?? 1e9));
	return { lineup, defense };
};

// Rotation turns in order; relievers who pitched the previous game rest; a replacement arm covers what's left
export const buildStaff = (pitchers: Player[], gameNumber: number, season: number, random: () => number) => {
	const ovr = (p: Player) => rt(p).ovr;
	const rotation = pitchers.filter(canStart).sort((a, b) => ovr(b) - ovr(a) || a.pid - b.pid).slice(0, 5);
	const starter: Player | undefined = rotation[gameNumber % 5];
	const starterOuts = starter ? clamp(Math.round(12 + (rt(starter).endu / 100) * 9 + (random() * 7 - 3)), 9, 24) : clamp(Math.round(12 + random() * 6), 9, 21);
	const lastPitched = (p: Player) => (p.minorStats?.season === season ? (p.minorStats.lp ?? -9) : -9);
	const pen = pitchers.filter((p) => !rotation.includes(p) && lastPitched(p) < gameNumber - 1).sort((a, b) => ovr(b) - ovr(a) || a.pid - b.pid);
	const staff: (Player | undefined)[] = [starter];
	const planned = [starterOuts];
	let outs = 27 - starterOuts;
	for (const p of pen) {
		if (outs <= 0 || staff.length >= 5) break;
		const n = Math.min(outs, 3 + Math.floor(random() * 4));
		staff.push(p);
		planned.push(n);
		outs -= n;
	}
	return { staff, planned };
};

type Runner = { b: Batter; earned: boolean; pitcher: Player | undefined };
export type SideTotals = { runs: number; h: number; hr: number; bb: number; so: number; pa: number; outs: number };
const zero = (): SideTotals => ({ runs: 0, h: 0, hr: 0, bb: 0, so: 0, pa: 0, outs: 0 });

/** One game. Mutates players' minorStats and both standings; returns per-side totals for checks. home = index 0. */
export const simMinorsGame = ({
	ctx, sides, season, standings, random = Math.random,
}: {
	ctx: Context;
	sides: [{ tid: number; players: Player[] }, { tid: number; players: Player[] }];
	season: number;
	standings: [MinorStanding, MinorStanding];
	random?: () => number;
}) => {
	const touched = new Set<Player>();
	const teams = sides.map((side, i) => {
		const { lineup, defense } = buildLineup(ctx, side.players.filter((p) => !isPitcherGroup(p)), random);
		const { staff, planned } = buildStaff(side.players.filter(isPitcherGroup), standings[i]!.g, season, random);
		return { lineup, defense, staff, planned, idx: 0, outsLeft: planned[0]!, appeared: new Set<Player>(), order: 0 };
	});
	const batting = [zero(), zero()]; // what each side did at bat
	const allowed = [zero(), zero()]; // what each side's pitchers allowed: the same events
	const score = [0, 0];
	for (const t of teams) for (const b of t.lineup) if (b.p) { line(b.p, season).g += 1; touched.add(b.p); }

	const enterPitcher = (side: number) => {
		const t = teams[side]!;
		const p = t.staff[t.idx];
		if (p && !t.appeared.has(p)) {
			t.appeared.add(p);
			const s = line(p, season);
			s.gp += 1;
			if (t.idx === 0) s.gs = (s.gs ?? 0) + 1;
			s.lp = standings[side]!.g;
			touched.add(p);
		}
	};
	const pitcherFor = (side: number) => {
		const t = teams[side]!;
		while (t.outsLeft <= 0) {
			t.idx += 1;
			if (t.idx >= t.staff.length) {
				// Out of planned arms (extra innings or a short staff): a replacement arm finishes
				t.staff.push(undefined);
				t.planned.push(Infinity);
			}
			t.outsLeft = t.planned[t.idx]!;
			enterPitcher(side);
		}
		return t.staff[t.idx];
	};
	enterPitcher(0);
	enterPitcher(1);

	const halfInning = (bat: number, inning: number) => {
		const fld = 1 - bat;
		const off = teams[bat]!, def = teams[fld]!;
		let outs = 0;
		const bases: (Runner | undefined)[] = [undefined, undefined, undefined];
		if (inning > 9) {
			// Extra innings: the previous batter starts on second; if he scores it's unearned
			const prev = off.lineup[(off.order + off.lineup.length - 1) % off.lineup.length]!;
			bases[1] = { b: prev, earned: false, pitcher: pitcherFor(fld) };
		}
		const score1 = (runner: Runner, rbiTo: Batter | undefined) => {
			score[bat]! += 1;
			batting[bat]!.runs += 1;
			allowed[fld]!.runs += 1;
			if (runner.earned && runner.pitcher) line(runner.pitcher, season).er += 1;
			if (rbiTo?.p) line(rbiTo.p, season).rbi += 1;
		};
		const recordOut = (pitcher: Player | undefined) => {
			outs += 1;
			batting[bat]!.outs += 1;
			allowed[fld]!.outs += 1;
			if (pitcher) line(pitcher, season).outs += 1;
			else standings[fld]!.rep.outs += 1;
			def.outsLeft -= 1;
		};
		while (outs < 3) {
			const pitcher = pitcherFor(fld);
			const batter = off.lineup[off.order % off.lineup.length]!;
			off.order += 1;
			const zc = zOf(ctx, batter.p, "con"), zp = zOf(ctx, batter.p, "hpw"), ze = zOf(ctx, batter.p, "eye"), zs = zOf(ctx, batter.p, "spd");
			const zpp = zOf(ctx, pitcher, "ppw"), zct = zOf(ctx, pitcher, "ctl"), zmv = zOf(ctx, pitcher, "mov");
			const pBB = clamp(0.085 + 0.025 * ze - 0.025 * zct, 0.025, 0.2);
			const pSO = clamp(0.22 - 0.045 * zc + 0.01 * zp + 0.045 * zpp, 0.06, 0.42);
			const pHR = clamp(0.028 + 0.017 * zp + 0.003 * zc - 0.012 * zmv, 0.002, 0.1);
			const pHitBIP = clamp(0.295 + 0.03 * zc + 0.01 * zs - 0.025 * zmv - 0.012 * def.defense, 0.18, 0.42);
			const pError = clamp(0.018 - 0.004 * def.defense, 0.005, 0.035);
			const bs = batter.p ? line(batter.p, season) : undefined;
			const ps = pitcher ? line(pitcher, season) : undefined;
			batting[bat]!.pa += 1;
			allowed[fld]!.pa += 1;
			if (bs) bs.pa += 1; else standings[bat]!.rep.pa += 1;
			if (ps) ps.bf += 1; else standings[fld]!.rep.bf += 1;
			const reach = (earned: boolean): Runner => ({ b: batter, earned, pitcher });
			// Stolen base / wild pitch before the outcome: runners move up a base (a run that scores is earned, no RBI)
			if ((bases[0] || bases[1] || bases[2]) && random() < 0.06) {
				if (bases[2]) score1(bases[2], undefined);
				bases[2] = bases[1];
				bases[1] = bases[0];
				bases[0] = undefined;
			}
			const x = random();
			if (x < pBB) {
				batting[bat]!.bb += 1; allowed[fld]!.bb += 1;
				if (bs) bs.bb += 1;
				if (ps) ps.bbA += 1;
				if (bases[0] && bases[1] && bases[2]) score1(bases[2], batter); // bases-loaded walk: RBI
				if (bases[0] && bases[1]) bases[2] = bases[1];
				if (bases[0]) bases[1] = bases[0];
				bases[0] = reach(true);
			} else if (x < pBB + pSO) {
				batting[bat]!.so += 1; allowed[fld]!.so += 1;
				if (bs) { bs.ab += 1; bs.so += 1; }
				if (ps) ps.soP += 1;
				recordOut(pitcher);
			} else if (x < pBB + pSO + pHR) {
				batting[bat]!.h += 1; batting[bat]!.hr += 1; allowed[fld]!.h += 1; allowed[fld]!.hr += 1;
				if (bs) { bs.ab += 1; bs.h += 1; bs.hr += 1; }
				if (ps) ps.ha += 1;
				for (const runner of bases) if (runner) score1(runner, batter);
				score1(reach(true), batter);
				bases[0] = bases[1] = bases[2] = undefined;
			} else if (random() < pHitBIP) {
				batting[bat]!.h += 1; allowed[fld]!.h += 1;
				if (bs) { bs.ab += 1; bs.h += 1; }
				if (ps) ps.ha += 1;
				const y = random();
				const pTriple = clamp(0.02 + 0.01 * zs, 0.005, 0.05);
				const pDouble = clamp(0.2 + 0.04 * zp, 0.1, 0.32);
				if (y < pTriple) {
					if (bs) bs.t += 1;
					for (const runner of bases) if (runner) score1(runner, batter);
					bases[0] = bases[1] = undefined;
					bases[2] = reach(true);
				} else if (y < pTriple + pDouble) {
					if (bs) bs.d += 1;
					if (bases[2]) score1(bases[2], batter);
					if (bases[1]) score1(bases[1], batter);
					const fromFirst = bases[0];
					bases[0] = bases[2] = undefined;
					bases[1] = reach(true);
					if (fromFirst) { if (random() < 0.45) score1(fromFirst, batter); else bases[2] = fromFirst; }
				} else {
					if (bases[2]) score1(bases[2], batter);
					const fromSecond = bases[1], fromFirst = bases[0];
					bases[0] = bases[1] = bases[2] = undefined;
					if (fromSecond) { if (random() < 0.6) score1(fromSecond, batter); else bases[2] = fromSecond; }
					// Runner from first: to third about a third of the time when it's open
					if (fromFirst) { if (!bases[2] && random() < 0.3) bases[2] = fromFirst; else bases[1] = fromFirst; }
					bases[0] = reach(true);
				}
			} else if (random() < pError) {
				// Reached on error: an at-bat without a hit; runners move up one; runs that score are unearned, no RBI
				if (bs) bs.ab += 1;
				if (bases[2]) score1({ ...bases[2], earned: false }, undefined);
				bases[2] = bases[1];
				bases[1] = bases[0];
				bases[0] = reach(false);
			} else {
				recordOut(pitcher);
				let sacrifice = false;
				if (outs < 3) {
					// Productive out: the runner from third scores (sacrifice fly or groundout, with an RBI; a sacrifice
					// fly is not an at-bat), other runners sometimes move up
					if (bases[2] && random() < 0.5) {
						const runner = bases[2];
						bases[2] = undefined;
						score1(runner, batter);
						sacrifice = random() < 0.6;
					}
					if (bases[1] && !bases[2] && random() < 0.4) { bases[2] = bases[1]; bases[1] = undefined; }
					if (bases[0] && !bases[1] && random() < 0.3) { bases[1] = bases[0]; bases[0] = undefined; }
				}
				if (bs && !sacrifice) bs.ab += 1;
			}
			// Walk-off: the home team stops batting as soon as it leads in the 9th or later
			if (bat === 0 && inning >= 9 && score[0]! > score[1]!) return;
		}
	};

	let inning = 1;
	for (; ; inning++) {
		halfInning(1, inning);
		if (inning >= 9 && score[0]! > score[1]!) break; // home already ahead: no bottom half
		halfInning(0, inning);
		if (inning >= 9 && score[0] !== score[1]) break;
		if (inning >= 60) break; // practically unreachable; the decision below still guarantees a result
	}
	if (score[0] === score[1]) {
		// Never seen in practice; keep one winner and one loser, and record the deciding run with the home team
		score[0] = score[0]! + 1;
		batting[0]!.runs += 1;
		allowed[1]!.runs += 1;
	}
	const homeWon = score[0]! > score[1]!;
	for (const [i, st] of standings.entries()) {
		st.g += 1;
		st.rs += score[i]!;
		st.ra += score[1 - i]!;
		if ((i === 0) === homeWon) st.w += 1;
		else st.l += 1;
	}
	return { touched, score: [score[0]!, score[1]!] as [number, number], batting, allowed, innings: inning };
};
